'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Login & verify ambient background.
 *
 * Design context (.impeccable.md): dark-first, warm-leaning navy surface,
 * brand pair (blue + orange) as the only chromatic accents. Replaces the
 * earlier rotating-blob background with a flight-paths motif that ties
 * directly to Nomu's travel domain.
 *
 * Stylized landmasses sit behind city pins (same transform). One flight
 * arc at a time — the same route is picked once and repeated on a loop
 * (new spawn remounts SMIL so the stroke animation restarts). Fully
 * suppressed under prefers-reduced-motion
 * (continents and dots stay; arcs do not).
 *
 * SSR note: arc state starts empty, so the server render and the first
 * client render produce identical DOM regardless of motion preference.
 */

interface City {
  x: number;
  y: number;
}

interface Arc {
  id: number;
  /** Remount the SMIL subtree so the next path actually animates (React + WebKit/Chrome). */
  spawnKey: number;
  origin: City;
  destination: City;
}

// Pin coordinates in the same "map space" as CONTINENTS (pre-transform);
// all points sit on the hand-authored land silhouettes, not a uniform grid.
const CITIES: readonly City[] = [
  { x: 12, y: 22 },
  { x: 20, y: 24 },
  { x: 26, y: 30 },
  { x: 16, y: 34 },
  { x: 30, y: 58 },
  { x: 78, y: 22 },
  { x: 88, y: 30 },
  { x: 75, y: 38 },
  { x: 84, y: 40 },
  { x: 86, y: 60 },
  { x: 10, y: 28 },
  { x: 90, y: 36 },
];

const ARC_LIFETIME_MS = 5500;
const LONG_SWEEP_MIN_DX = 50;

// Stylized continents authored on a rough equirectangular projection
// (longitude → x, latitude → y on the 100x100 viewBox) so the silhouettes
// stay loyal to a real world map: Brazil bulges east, India hangs south,
// Cape of Good Hope at Africa's southern tip, Cape York points north out
// of Australia, etc. Polylines are used (not beziers) because the static
// soften filter rounds the corners — this keeps the path data readable
// while still producing organic shapes when rendered.
// Single transform (vs editing every point): larger overall footprint and
// extra horizontal spread so the map fills more of the canvas; centered on
// the viewBox so left/right landmasses stay balanced.
const CONTINENT_GROUP_TRANSFORM =
  'translate(50 53) scale(1.24 1.1) translate(-50 -50)';

const CONTINENTS: readonly string[] = [
  // Eurasia: Iberia → Scandinavia → Siberia → Kamchatka → Japan → SE Asia
  // → India → Arabia → Mediterranean → back to Iberia.
  'M 47 34 L 49 31 L 51 26 L 57 21 L 70 19 L 85 18 L 98 24 L 94 30 L 90 33 L 86 38 L 84 38 L 82 41 L 80 47 L 79 50 L 72 47 L 70 42 L 65 44 L 62 45 L 59 38 L 60 36 L 56 35 L 54 35 L 49 36 Z',
  // Africa: Mediterranean coast → Horn of Africa → Mozambique → Cape of
  // Good Hope → Namibia → Gulf of Guinea → Senegal → Morocco.
  'M 53 35 L 58 38 L 64 45 L 61 51 L 60 59 L 55 64 L 53 59 L 53 54 L 52 50 L 50 48 L 45 44 L 46 40 L 48 36 Z',
  // North America: Alaska → Canadian Arctic → Newfoundland → US East →
  // Florida → Gulf → Yucatan → Panama → Mexico W → Baja → California →
  // Pacific NW → Alaska.
  'M 4 22 L 14 16 L 28 16 L 35 26 L 30 33 L 28 39 L 24 38 L 26 42 L 28 47 L 22 43 L 19 40 L 16 35 L 16 28 L 8 23 Z',
  // South America: Venezuela coast → Brazil NE bulge → Brazil SE →
  // Uruguay → Tierra del Fuego → Chile coast → Ecuador → Colombia.
  'M 30 47 L 36 49 L 41 53 L 38 59 L 35 64 L 33 67 L 32 73 L 30 67 L 30 56 L 28 50 L 29 48 Z',
  // Australia: NW Pilbara → Darwin → Cape York → Brisbane → Sydney →
  // Adelaide → Perth → W central.
  'M 82 59 L 86 55 L 89 54 L 92 60 L 91 65 L 87 64 L 83 63 L 81 60 Z',
  // Greenland: small island top-of-map.
  'M 33 18 L 38 16 L 40 21 L 38 25 L 34 23 Z',
  // Antarctica: thin southern strip.
  'M 18 90 L 30 87 L 50 86 L 70 87 L 92 89 L 90 92 L 70 93 L 50 92 L 30 92 L 18 90 Z',
];

function sameCity(a: City, b: City): boolean {
  return a.x === b.x && a.y === b.y;
}

function pickRandomCity<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]!;
}

function pickDistinctPair(pool: readonly City[]): { origin: City; destination: City } {
  const origin = pickRandomCity(pool);
  const candidates = pool.filter((c) => !sameCity(c, origin));
  const destination =
    candidates[Math.floor(Math.random() * Math.max(1, candidates.length))] ?? origin;
  return { origin, destination };
}

function pickLongSweepPair(): { origin: City; destination: City } {
  const longCandidates: Array<{ origin: City; destination: City }> = [];
  for (const origin of CITIES) {
    for (const destination of CITIES) {
      if (sameCity(origin, destination)) continue;
      if (Math.abs(destination.x - origin.x) > LONG_SWEEP_MIN_DX) {
        longCandidates.push({ origin, destination });
      }
    }
  }
  if (longCandidates.length > 0) {
    return pickRandomCity(longCandidates);
  }
  return pickDistinctPair(CITIES);
}

function buildArcPath(a: City, b: City): string {
  const dx = b.x - a.x;
  const midX = (a.x + b.x) / 2;
  const lift = Math.max(8, Math.abs(dx) * 0.35);
  const midY = Math.min(a.y, b.y) - lift;
  return `M ${a.x} ${a.y} Q ${midX} ${midY} ${b.x} ${b.y}`;
}

const PREFERS_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * The spawn loop must not depend on React state (e.g. a media query hook) or
 * the effect re-runs, clears the interval, and the next path never starts.
 * Read the preference inside each tick instead; the interval is created once
 * and cleared only on unmount.
 */
function prefersReducedMotionNow(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(PREFERS_REDUCED_MOTION).matches;
}

export function FlightPathsBackground() {
  const arcIdRef = useRef(0);
  const fixedRouteRef = useRef<{ origin: City; destination: City } | null>(null);
  const [arcs, setArcs] = useState<Arc[]>([]);

  useEffect(() => {
    let cancelled = false;
    let nextTimeout: number | null = null;

    const tick = () => {
      if (cancelled) return;
      if (prefersReducedMotionNow()) {
        setArcs([]);
        return;
      }
      try {
        if (!fixedRouteRef.current) {
          fixedRouteRef.current = pickLongSweepPair();
        }
        const { origin, destination } = fixedRouteRef.current;
        arcIdRef.current += 1;
        const id = arcIdRef.current;
        const spawnKey =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        // `spawnKey` + `key` on the arc group remounts the SMIL subtree. Do not use
        // flushSync here: it is invalid when called from a synchronous `tick()` that
        // still runs as part of the `useEffect` work (e.g. the initial call).
        setArcs([{ id, spawnKey, origin, destination }]);
      } catch {
        if (!cancelled) {
          setArcs([]);
        }
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      nextTimeout = window.setTimeout(() => {
        if (cancelled) return;
        tick();
        if (!cancelled) scheduleNext();
      }, ARC_LIFETIME_MS);
    };

    tick();
    scheduleNext();

    return () => {
      cancelled = true;
      if (nextTimeout) clearTimeout(nextTimeout);
    };
  }, []);

  return (
    <div
      aria-hidden
      data-testid="flight-paths-background"
      className="fixed inset-0 overflow-hidden"
      style={{
        // Keep base paint separate so vignette layers can't be hidden under it.
        background:
          'radial-gradient(120% 120% at 0% 0%, hsl(225 35% 10%) 0%, hsl(231 38% 7%) 60%, hsl(225 30% 5%) 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(circle at 30% 20%, hsl(var(--brand-secondary) / 0.10), transparent 60%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(circle at 70% 80%, hsl(var(--brand) / 0.12), transparent 60%)',
        }}
      />
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 z-[2] h-full w-full scale-[1.22] md:scale-100"
      >
        <defs>
          <filter
            id="flight-glow"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="0.25" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Rounds the continent polylines into organic landmass shapes
              while leaving the silhouette readable. Applied once to the
              static group, so cost is paid only on mount/resize. */}
          <filter
            id="continents-soften"
            x="-40%"
            y="-40%"
            width="180%"
            height="180%"
          >
            <feGaussianBlur stdDeviation="1.05" />
          </filter>
        </defs>

        <g filter="url(#continents-soften)">
          <g transform={CONTINENT_GROUP_TRANSFORM}>
            {CONTINENTS.map((d, i) => (
              <path
                key={`continent-${i}`}
                d={d}
                fill="hsl(var(--brand))"
                opacity={0.11}
              />
            ))}
          </g>
        </g>

        <g transform={CONTINENT_GROUP_TRANSFORM}>
          {CITIES.map((c, i) => (
            <g key={`city-${i}`}>
              <circle
                cx={c.x}
                cy={c.y}
                r={0.72}
                fill="hsl(var(--brand))"
                opacity={0.15}
              />
              <circle
                cx={c.x}
                cy={c.y}
                r={0.3}
                fill="hsl(var(--brand))"
                opacity={0.5}
              />
            </g>
          ))}

          {arcs.map((arc) => {
            const pathId = `flight-arc-${arc.id}`;
            const gradId = `flight-grad-${arc.id}`;
            return (
              <g key={`${arc.id}-${arc.spawnKey}`}>
                <linearGradient
                  id={gradId}
                  gradientUnits="userSpaceOnUse"
                  x1={arc.origin.x}
                  y1={arc.origin.y}
                  x2={arc.destination.x}
                  y2={arc.destination.y}
                >
                  <stop offset="0%" stopColor="hsl(var(--brand-secondary))" />
                  <stop offset="100%" stopColor="hsl(var(--brand))" />
                </linearGradient>

                <path
                  id={pathId}
                  d={buildArcPath(arc.origin, arc.destination)}
                  fill="none"
                  stroke={`url(#${gradId})`}
                  strokeWidth={0.28}
                  strokeLinecap="round"
                  pathLength={100}
                  strokeDasharray={100}
                  strokeDashoffset={100}
                  opacity={1}
                  filter="url(#flight-glow)"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={100}
                    to={0}
                    dur="2.5s"
                    begin="0s"
                    fill="freeze"
                  />
                  <animate
                    attributeName="opacity"
                    values="1;1;0"
                    keyTimes="0;0.7;1"
                    dur="5.5s"
                    begin="0s"
                    fill="freeze"
                  />
                </path>

                <circle
                  cx={arc.origin.x}
                  cy={arc.origin.y}
                  r={0.4}
                  fill="hsl(var(--brand-secondary))"
                  opacity={1}
                >
                  <animate
                    attributeName="r"
                    values="0.4;1.3;0.4"
                    dur="1.4s"
                    begin="0s"
                    fill="freeze"
                  />
                  <animate
                    attributeName="opacity"
                    values="1;0.5;0"
                    dur="1.4s"
                    begin="0s"
                    fill="freeze"
                  />
                </circle>

                <circle r={0.55} fill="white" opacity={0} filter="url(#flight-glow)">
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.1;0.85;1"
                    dur="3.2s"
                    begin="0.3s"
                    fill="freeze"
                  />
                  <animateMotion dur="3.2s" begin="0.3s" fill="freeze" rotate="auto">
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                </circle>

                <circle
                  cx={arc.destination.x}
                  cy={arc.destination.y}
                  r={0.4}
                  fill="hsl(var(--brand))"
                  opacity={0}
                >
                  <animate
                    attributeName="r"
                    values="0.4;1.5;2.2"
                    dur="2s"
                    begin="3.5s"
                    fill="freeze"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;0"
                    dur="2s"
                    begin="3.5s"
                    fill="freeze"
                  />
                </circle>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
